import { Menu as MenuPrimitive } from '@base-ui/react/menu'
import { Check, ChevronRight } from 'lucide-react'

import { cn } from '@/lib/utils'

function DropdownMenu({ ...props }: MenuPrimitive.Root.Props) {
  return <MenuPrimitive.Root data-slot="dropdown-menu" {...props} />
}

function DropdownMenuTrigger({ ...props }: MenuPrimitive.Trigger.Props) {
  return (
    <MenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />
  )
}

function DropdownMenuSub({
  onOpenChange,
  ...props
}: MenuPrimitive.SubmenuRoot.Props) {
  return (
    <MenuPrimitive.SubmenuRoot
      data-slot="dropdown-menu-sub"
      onOpenChange={(open, details) => {
        if (!open && details.reason === 'trigger-hover') {
          details.cancel()
          return
        }
        onOpenChange?.(open, details)
      }}
      {...props}
    />
  )
}

function DropdownMenuSubTrigger({
  className,
  children,
  openOnHover = false,
  delay = openOnHover ? 0 : undefined,
  closeDelay = openOnHover ? 150 : undefined,
  ...props
}: MenuPrimitive.SubmenuTrigger.Props) {
  return (
    <MenuPrimitive.SubmenuTrigger
      data-slot="dropdown-menu-sub-trigger"
      className={cn(
        itemClass,
        'text-foreground data-popup-open:bg-accent data-highlighted:bg-accent',
        className,
      )}
      openOnHover={openOnHover}
      delay={delay}
      closeDelay={closeDelay}
      {...props}
    >
      {children}
      <ChevronRight aria-hidden className="text-muted-foreground ml-auto size-3.5" />
    </MenuPrimitive.SubmenuTrigger>
  )
}

function DropdownMenuContent({
  className,
  align = 'end',
  side = 'bottom',
  sideOffset = 4,
  keepMounted,
  ...props
}: MenuPrimitive.Popup.Props &
  Pick<MenuPrimitive.Positioner.Props, 'align' | 'side' | 'sideOffset'> &
  Pick<MenuPrimitive.Portal.Props, 'keepMounted'>) {
  return (
    <MenuPrimitive.Portal keepMounted={keepMounted}>
      <MenuPrimitive.Positioner
        align={align}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-50"
      >
        <MenuPrimitive.Popup
          data-slot="dropdown-menu-content"
          className={cn(
            'bg-popover text-popover-foreground ring-border data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 flex min-w-44 origin-(--transform-origin) flex-col rounded-lg p-1 text-xs shadow-md ring-1 outline-hidden duration-100',
            className,
          )}
          {...props}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

function DropdownMenuSubContent({
  className,
  ...props
}: MenuPrimitive.Popup.Props) {
  return (
    <DropdownMenuContent
      data-slot="dropdown-menu-sub-content"
      side="right"
      align="start"
      sideOffset={-2}
      className={className}
      {...props}
    />
  )
}

const itemClass =
  'flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 select-none outline-hidden data-disabled:cursor-not-allowed data-disabled:opacity-50 data-highlighted:ring-2 data-highlighted:ring-foreground data-highlighted:ring-inset'

function DropdownMenuItem({
  className,
  variant = 'default',
  ...props
}: MenuPrimitive.Item.Props & { variant?: 'default' | 'destructive' }) {
  return (
    <MenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={cn(
        itemClass,
        variant === 'destructive'
          ? 'text-destructive data-highlighted:bg-destructive/15'
          : 'text-foreground data-highlighted:bg-accent',
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuLinkItem({
  className,
  ...props
}: MenuPrimitive.LinkItem.Props) {
  return (
    <MenuPrimitive.LinkItem
      data-slot="dropdown-menu-link-item"
      className={cn(
        itemClass,
        'text-foreground data-highlighted:bg-accent',
        className,
      )}
      {...props}
    />
  )
}

function DropdownMenuRadioGroup({ ...props }: MenuPrimitive.RadioGroup.Props) {
  return (
    <MenuPrimitive.RadioGroup data-slot="dropdown-menu-radio-group" {...props} />
  )
}

function DropdownMenuGroup({ ...props }: MenuPrimitive.Group.Props) {
  return <MenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
}

function DropdownMenuGroupLabel({
  className,
  ...props
}: MenuPrimitive.GroupLabel.Props) {
  return (
    <MenuPrimitive.GroupLabel
      data-slot="dropdown-menu-group-label"
      className={cn('text-muted-foreground px-2 pt-1.5 pb-1 text-[10px] font-medium', className)}
      {...props}
    />
  )
}

function DropdownMenuRadioItem({
  className,
  children,
  indicator = 'dot',
  ...props
}: MenuPrimitive.RadioItem.Props & { indicator?: 'dot' | 'check' }) {
  return (
    <MenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        itemClass,
        'data-checked:font-medium data-highlighted:bg-accent justify-between',
        indicator === 'check'
          ? 'text-foreground'
          : 'text-muted-foreground data-checked:text-foreground',
        className,
      )}
      {...props}
    >
      <span className="flex items-center gap-2">{children}</span>
      <MenuPrimitive.RadioItemIndicator data-slot="dropdown-menu-radio-item-indicator">
        {indicator === 'check'
          ? <Check className="text-primary size-3.5" />
          : <span className="bg-primary block size-1.5 rounded-full" />}
      </MenuPrimitive.RadioItemIndicator>
    </MenuPrimitive.RadioItem>
  )
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  indicator = 'check',
  ...props
}: MenuPrimitive.CheckboxItem.Props & { indicator?: 'check' | 'checkbox' }) {
  return (
    <MenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        itemClass,
        'text-muted-foreground data-checked:text-foreground data-checked:font-medium data-highlighted:bg-accent justify-between',
        className,
      )}
      checked={checked}
      {...props}
    >
      {indicator === 'checkbox' ? (
        <span
          data-slot="dropdown-menu-checkbox-control"
          className={cn(
            'border-input bg-background flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border',
            checked && 'border-primary bg-primary text-primary-foreground',
          )}
        >
          <MenuPrimitive.CheckboxItemIndicator>
            <Check aria-hidden className="size-2.5" />
          </MenuPrimitive.CheckboxItemIndicator>
        </span>
      ) : null}
      <span className="flex min-w-0 flex-1 items-center gap-2">{children}</span>
      {indicator === 'check' ? (
        <MenuPrimitive.CheckboxItemIndicator>
          <Check className="text-primary size-3.5" />
        </MenuPrimitive.CheckboxItemIndicator>
      ) : null}
    </MenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: MenuPrimitive.Separator.Props) {
  return (
    <MenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('bg-border my-1 h-px', className)}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuGroupLabel,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
}
